import { useEffect, useState } from 'react'
import { PermissionAction, permissionsService, PermissionType } from '../services/permissions.service'
import { storagePathService } from '../services/storage-path.service'
import { StoragePath } from '../types'

let globalCache: StoragePath[] | null = null
let globalCachePromise: Promise<StoragePath[]> | null = null
let cacheListeners: Set<() => void> = new Set()

const loadCache = async (): Promise<StoragePath[]> => {
  if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.StoragePath)) {
    return []
  }
  if (globalCache) {
    return globalCache
  }

  if (globalCachePromise) {
    return globalCachePromise
  }

  globalCachePromise = storagePathService
    .listAll()
    .then((response) => {
      globalCache = response.results || []
      globalCachePromise = null
      cacheListeners.forEach((listener) => listener())
      return globalCache
    })
    .catch(() => {
      globalCachePromise = null
      return []
    })

  return globalCachePromise
}

export const useStoragePathsCache = (): { storagePaths: StoragePath[]; loaded: boolean } => {
  const [storagePaths, setStoragePaths] = useState<StoragePath[]>(globalCache || [])
  const [loaded, setLoaded] = useState(!!globalCache)

  useEffect(() => {
    if (globalCache) {
      setStoragePaths(globalCache)
      setLoaded(true)
      return
    }

    const listener = () => {
      if (globalCache) {
        setStoragePaths(globalCache)
        setLoaded(true)
      }
    }

    cacheListeners.add(listener)

    loadCache().then((cached) => {
      setStoragePaths(cached)
      setLoaded(true)
    })

    return () => {
      cacheListeners.delete(listener)
    }
  }, [])

  return { storagePaths, loaded }
}
